/**
 * 「这次作业截止到**哪一天**」的纯规则：默认策略 + 显示文案。
 *
 * ## 为什么需要这个文件（用户原话）
 *
 * > 布置孩子的学校作业时，默认作业是要当天完成的。如果当天没完成，第二天就会显示超时。
 * > 现在是星期五，布置的作业可以在星期五晚上，以及星期六、星期天这两天一夜内完成，
 * > 都没有问题。但如果按照你之前的规则，就会超时。
 *
 * 改造前的模型只有一个「**时刻**」：科目的 `node.due_time`（默认 `23:59`，
 * 见 `HOMEWORK_DEFAULT_DUE_TIME`），而 `checkin.due_at` 由
 * 「**布置日** + 该时刻」推导出来（`localDateTimeUtc(dueDate, dueTime)`）。
 * 也就是说**截止日永远等于布置日** —— 周五布置的作业，周六 00:00 一过就算超时。
 *
 * 现在多了一个「**日期**」维度：`dueDate`（存在作业科目节点的 `node.due_date`，
 * 见 `apps/api/src/modules/homework/homework.service.ts` 的说明）。
 * 本文件是"默认截止日怎么算"与"怎么把它说成人话"的**唯一一份**实现：
 * 家长端表单、家长端查看、学生端都引用它，不会出现"表单里写着周日、孩子那边写着今天"。
 *
 * ⚠️ 它是**独立一列**（迁移 0012），刻意**不**借用科目节点的 `end_date`：
 * `start_date` / `end_date` 属于节点树的计划区间，触发器 `guard_node_tree()`
 * 强制"子级区间不得超出上级"，把截止日期放那里必然产生
 * `NODE_DATE_OUT_OF_PARENT_RANGE`（用户实测报过）。
 *
 * ## 三种默认策略（**一处常量切换**：`HOMEWORK_DUE_POLICY`）
 *
 * | 策略 | 语义 | 周末表现 |
 * | --- | --- | --- |
 * | `same-day` | 永远是布置日（改造前的行为） | 周五布置 → 周五截止（周六就超时） |
 * | `weekend-roll` | 当天，**周五/周六自动滚到周日** | 周五/周六布置 → 周日截止 |
 * | `next-school-day-eve`（**当前采用**） | **下一个上学日的前一晚** | 周五/周六布置 → 周日截止；周日布置 → 当天 |
 *
 * `next-school-day-eve` 是 `weekend-roll` 的一般化：它不写死"周五、周六、周日"，
 * 而是问一句"布置日的**下一个上学日**是哪天，它的前一晚就是截止"。
 * 在一周上 5 天学（`HOMEWORK_SCHOOL_WEEKDAYS = [1..5]`）时两者对
 * 周一到周日**每一天**的结果完全一致，但前者表达的是规则、后者表达的是一串特例：
 *  - 周一 ~ 周四布置：下一个上学日就是明天 → **当天**（与改造前逐日相同，行为不变）；
 *  - 周五布置：下一个上学日是周一 → **周日**；
 *  - 周六布置：同上 → **周日**；
 *  - 周日布置：下一个上学日是周一 → **周日**（= 当天）。
 *
 * ⚠️ 周日布置的作业**当天截止**是有意的：周一要交的作业，前一晚就是周日，
 * 换成"周一 24:00"意味着孩子可以在周一白天补 —— 那已经不是"交作业"了。
 * 想留到周一晚上的家长可以**自己把截止日期改成周一**（这就是"能指定日期"的用处）。
 *
 * ## 法定节假日 / 调休 / 寒暑假：本规则会**退化**成什么
 *
 * 这里**只有星期几**这一个信息（刻意不接任何节假日 API，见任务约束"不新增外部依赖"），
 * 所以：
 *  - **国庆/春节这类长假**：布置日若是周五/周六，仍然只滚到"下一个上学日的前一晚"，
 *    而我们并不知道 10-01~10-07 放假 → **退化成"按普通周末处理"**（周日截止），
 *    不会自动变成"整个假期都能做"；
 *  - **调休上班的周末**（周六要上学）：我们会把周六当休息日 → 周五布置的作业
 *    仍然滚到周日，比真实情况**宽一天**（宁可宽一天，也不要严一天——
 *    严了就变成用户报的那种"还来不及做就超时"）；
 *  - **寒暑假**：同上，退化成"周末规则"。
 *
 * **兜底永远是同一件事：家长可以手动指定截止日期**（表单里的「📅 截止日期」）。
 * 这个默认策略只负责"绝大多数情况下不用改"，它不假装自己懂中国的节假日安排。
 */

import { addDays, weekdayOf } from './time.js';

// ================================================================ 策略与常量

/** 默认截止策略（**切换这一处即可**，见文件头那张表）。 */
export type HomeworkDuePolicy = 'same-day' | 'weekend-roll' | 'next-school-day-eve';

/**
 * **当前采用的默认策略**（默认截止日 = 下一个上学日的前一晚）。
 *
 * 想换成另外两种：把这一行的值改成 `'weekend-roll'` 或 `'same-day'` 即可 ——
 * 默认值的计算、快捷选项、提示文案、测试都只读这一个常量，不需要改别的地方。
 * 三个方案的利弊对比见交付说明里的表格。
 */
export const HOMEWORK_DUE_POLICY: HomeworkDuePolicy = 'next-school-day-eve';

/**
 * 一周里哪几天要上学（`0` = 周日 … `6` = 周六，与 `weekdayOf()` 同口径）。
 *
 * ⚠️ 它**不是**中国的校历，只是"周末不上学"这个最普遍的假设；
 * 节假日/调休/寒暑假刻意不在这里处理（见文件头"退化表现"）。
 */
export const HOMEWORK_SCHOOL_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

/** 找"下一个上学日"时最多往后看几天（找不到就退化成当天，见下）。 */
export const HOMEWORK_DUE_LOOKAHEAD_DAYS = 14;

/**
 * 截止日期距离布置日**最多**多少天。
 *
 * 上限的作用是挡住"手滑把年份打错"（例如 `2126-10-18`）：那样的作业
 * **永远不会超时**，于是一条本该被完成的作业会安静地躺在孩子的清单里，
 * 既不算超时也不会被家长注意到 —— 一个静默的时间黑洞。
 * 一年足够覆盖寒暑假（家长手动指定时也用得上），再多就没有实际意义了。
 *
 * 服务端（`resolveSubjectDueDate`）与家长端本地校验（`validateHomeworkDraft`）
 * 共用这一个数，不会出现"前端放行、后端拒绝"。
 */
export const HOMEWORK_MAX_DUE_DATE_DAYS = 366;

/**
 * `'YYYY-MM-DD'` 且**真实存在**的日期（`2026-02-30` 这类不存在的日期返回 false）。
 *
 * 为什么不能只靠正则：`new Date('2026-02-30T00:00:00Z')` 会**静默滚到** 3 月 2 日，
 * 于是"日期不存在"这件事会被藏进一个看起来合法的日子里
 * （与 `time.ts` 的 `parseLocalDate` 同一条理由，这里只是不抛异常的版本）。
 */
export function isLocalDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  try {
    return addDays(value, 0) === value;
  } catch {
    return false;
  }
}

/** 星期几的中文（下标 = `weekdayOf()` 的返回值）。 */
export const WEEKDAY_LABELS: readonly string[] = [
  '周日',
  '周一',
  '周二',
  '周三',
  '周四',
  '周五',
  '周六',
];

/** 这一天要上学吗。日期非法时返回 false（宁可退化成当天，也不要算出奇怪的日子）。 */
export function isSchoolDay(
  localDate: string,
  schoolWeekdays: readonly number[] = HOMEWORK_SCHOOL_WEEKDAYS,
): boolean {
  const weekday = weekdayOf(localDate);
  return Number.isFinite(weekday) && schoolWeekdays.includes(weekday);
}

// ================================================================ 默认截止日

/**
 * **默认截止日期**（纯函数，本文件的核心）。
 *
 * 语义（三种策略，见文件头）：
 *  - `same-day`：就是布置日；
 *  - `weekend-roll`：布置日是周五/周六 → 这一周的周日；否则当天；
 *  - `next-school-day-eve`：从布置日往后找**第一个上学日**，它的**前一天**就是截止日。
 *
 * ⚠️ 找不到上学日时（`schoolWeekdays` 配成了空数组之类）**退化成当天**，
 * 而不是抛异常或返回一个空值：默认值算不出来时，"当天完成"是改造前的既有行为，
 * 也是唯一不会让家长看到"奇怪的日期"的兜底。
 */
export function defaultHomeworkDueDate(
  planDate: string,
  policy: HomeworkDuePolicy = HOMEWORK_DUE_POLICY,
  schoolWeekdays: readonly number[] = HOMEWORK_SCHOOL_WEEKDAYS,
): string {
  if (policy === 'same-day') return planDate;

  if (policy === 'weekend-roll') {
    const weekday = weekdayOf(planDate);
    // 周五（5）→ 周日（+2）；周六（6）→ 周日（+1）；其余原样
    if (weekday === 5 || weekday === 6) return addDays(planDate, (7 - weekday) % 7);
    return planDate;
  }

  for (let step = 1; step <= HOMEWORK_DUE_LOOKAHEAD_DAYS; step++) {
    const candidate = addDays(planDate, step);
    if (isSchoolDay(candidate, schoolWeekdays)) return addDays(candidate, -1);
  }
  return planDate;
}

// ================================================================ 截止日的**唯一**口径

/**
 * 一个作业项**实际**的截止日期：`node.due_date ?? 布置日`。
 *
 * ⚠️ 这是全项目推导 `checkin.due_at` 时"用哪一天"的**唯一**口径，四条路径都引用它：
 *   - `apps/api/.../homework.service.ts` 的 `materializeHomeworkCheckins()`（保存时补实例）
 *     与 `syncHomeworkDueAt()`（改完日期/时刻后纠正未终结的实例）；
 *   - `apps/api/.../checkins.service.ts` 的 `materializeDueCheckins()`（学生打开今日清单时补）；
 *   - `apps/worker/src/jobs.ts` 的 `generateDailyCheckins()`（每天 0 点批量生成）。
 *
 * 四处都是 `INSERT ... ON CONFLICT DO NOTHING`（**谁先写谁说了算**），
 * 所以口径必须完全一致 —— 这个项目已经因为"同一个概念两处各推一遍"反复踩坑。
 *
 * 返回日期字符串而不是 `Date`：时区换算仍由调用方用
 * `localDateTimeUtc(date, time, timeZone)` 完成（家庭时区换算的唯一入口）。
 */
export function homeworkDueDateOf(row: {
  /** 作业是哪一天的（`checkin.due_date`）。 */
  planDate: string;
  /** 截止日（`node.due_date`）；空 = 与 `planDate` 同一天。 */
  dueDate?: string | null;
}): string {
  const raw = (row.dueDate ?? '').trim();
  return raw === '' ? row.planDate : raw;
}

// ================================================================ 文案

/**
 * 截止日期的**短标签**：`'当天'` / `'周日 10-18'`（跨年时写成 `'周五 2027-01-01'`）。
 *
 * 为什么带星期几：家长说"周五布置的作业留到周日"时，脑子里就是星期几；
 * 而"周日"单独一个词在手机上很容易被误读成"下周日"，所以**星期几 + 月-日**一起给，
 * 既不啰嗦又不会歧义（跨年时补上年份，避免"01-02"看不出是哪一年）。
 */
export function homeworkDueDateLabel(dueDate: string | null | undefined, planDate: string): string {
  if (!dueDate || dueDate === planDate) return '当天';
  const weekday = WEEKDAY_LABELS[weekdayOf(dueDate)];
  const sameYear = dueDate.slice(0, 4) === planDate.slice(0, 4);
  const datePart = sameYear ? dueDate.slice(5) : dueDate;
  return weekday ? `${weekday} ${datePart}` : datePart;
}

/** 一个科目的截止信息（家长端与展示口径的共用入参形状）。 */
export interface HomeworkDueDescription {
  /** 这份作业是哪一天的（`checkin.due_date` / 作业单的 `start_date`）。 */
  planDate: string;
  /** 截止日期（'YYYY-MM-DD'）；空 = 与 `planDate` 同一天。 */
  dueDate?: string | null;
  /** 与 `HomeworkDueTimeSource` 同值（shared 不反向依赖 api，所以这里写成字面量联合）。 */
  dueTimeSource: 'explicit' | 'duration' | 'dayEnd' | 'none';
  /** 'HH:mm'（服务端 dayEnd 的 23:59 **不算**"家长填过"）。 */
  dueTime?: string | null;
  durationMinutes?: number | null;
}

/**
 * **家长端/查看口径**的截止短语（`时间` 折叠行的摘要、查看页那一行都用它）。
 *
 * ⚠️ 两条既有约定必须逐字保住（用户确认过，见 `docs/接口契约大全`）：
 *  1. `dayEnd` 且截止日就是布置日 → 显示「**当天完成**」，**绝不能显示 23:59**；
 *  2. 家长填了明确时刻 → 显示 `19:00 截止`。
 * 只有在"截止日不是布置日"时才多说一个日期（`周日 10-18 结束前完成`）——
 * 那正是这次新增的语义，不说出来家长就不知道作业留到了哪一天。
 */
export function describeHomeworkDueText(input: HomeworkDueDescription): string {
  const sameDay = !input.dueDate || input.dueDate === input.planDate;
  const label = homeworkDueDateLabel(input.dueDate, input.planDate);
  const dueTime = (input.dueTime ?? '').trim();

  switch (input.dueTimeSource) {
    case 'dayEnd':
      return sameDay ? '当天完成' : `${label} 结束前完成`;
    case 'duration': {
      const minutes = input.durationMinutes;
      if (minutes !== null && minutes !== undefined) {
        return sameDay ? `${minutes} 分钟` : `${label} ${minutes} 分钟`;
      }
      if (dueTime !== '') return sameDay ? `${dueTime} 截止` : `${label} ${dueTime} 截止`;
      return sameDay ? '当天完成' : `${label} 结束前完成`;
    }
    case 'explicit':
      // 改造前的口径：没有 dueTime 时**什么都不说**（不编一个时刻出来）
      if (dueTime === '') return '';
      return sameDay ? `${dueTime} 截止` : `${label} ${dueTime} 截止`;
    default:
      // `none`：理论上不该出现（作业模块写入的科目永远有 due_time）
      return sameDay ? '没设截止时间' : `${label} 截止`;
  }
}

/**
 * **学生端**的截止说法（**不带任何压力性措辞** —— 不写"还剩""尽快""别拖"）。
 *
 * 学生端原来**完全不显示截止日期**（只有超时之后才出现一个红色/琥珀色徽章），
 * 而"周五的作业周日截止"这件事孩子必须知道，否则他周六看到超时徽章会以为自己已经迟了。
 * 所以这里只说两件事：**到哪天为止**、**做完就行**。
 */
export function describeStudentHomeworkDueText(input: HomeworkDueDescription): string {
  const sameDay = !input.dueDate || input.dueDate === input.planDate;
  const label = homeworkDueDateLabel(input.dueDate, input.planDate);
  const dueTime = (input.dueTime ?? '').trim();
  const explicitTime = input.dueTimeSource === 'explicit' || input.dueTimeSource === 'duration';

  if (sameDay) {
    if (explicitTime && dueTime !== '') return `今天 ${dueTime} 前做完就行`;
    return '今天做完就行';
  }
  if (explicitTime && dueTime !== '') return `${label} ${dueTime} 前做完就行`;
  return `${label} 结束前做完就行`;
}

/**
 * 「截止时刻」输入框下面的提示语（两个输入框共用同一句，避免两处各写一遍）。
 *
 * ⚠️ 改动前的固定文案是「留空 = 当天完成（不显示 23:59）」，它承载的语义**不能丢**：
 * `dueTimeSource === 'dayEnd'` 时服务端落的是 23:59，而界面必须说"当天完成"。
 * 现在多了日期维度，所以只有"截止日就是布置日"时才逐字保留旧文案。
 */
export function describeHomeworkDueTimeHint(
  dueDate: string | null | undefined,
  planDate: string,
): string {
  const label = homeworkDueDateLabel(dueDate, planDate);
  return label === '当天'
    ? '留空 = 当天完成（不显示 23:59）'
    : `留空 = 到「${label}」结束前完成（不显示 23:59）`;
}

/**
 * 「📅 截止日期」这一行下面的**默认值说明**（让家长一眼看懂"为什么默认是周日"）。
 *
 * 要求是"必须把默认值明确显示出来"：默认值本身由输入框显示（`周日 10-18`），
 * 这一句负责解释它是**怎么来的**，否则家长会以为系统算错了日子。
 */
export function describeHomeworkDueDefault(
  planDate: string,
  policy: HomeworkDuePolicy = HOMEWORK_DUE_POLICY,
): string {
  const due = defaultHomeworkDueDate(planDate, policy);
  if (due === planDate) return '默认：当天完成';
  const label = homeworkDueDateLabel(due, planDate);
  return policy === 'same-day'
    ? `默认：${label}`
    : `默认：${label}（周五、周六布置的作业留到周日，整个周末都能做）`;
}

/** 「📅 截止日期」行里那排快捷选项。 */
export interface HomeworkDueQuickOption {
  /** 'YYYY-MM-DD'。 */
  value: string;
  /** 按钮上的字（手机上要一眼看懂，所以带日期而不是只写"默认"）。 */
  label: string;
}

/**
 * 「**建议**把这次作业留到周末」——**只有策略是 `same-day`（方案丙）时才有值**。
 *
 * 方案丙 = "默认仍然是当天，但界面上醒目提示'这一天是周五，建议改成周日'，
 * 由家长一键采纳"。它存在的意义是把"周末要不要放宽"这个决定**交给家长**，
 * 而不是系统替他决定 —— 代价是每个周五/周六都要家长点一下。
 *
 * 因此这个函数是"把 `HOMEWORK_DUE_POLICY` 切成 `'same-day'` 之后界面仍然完整"的那一半：
 *   - 策略是 `next-school-day-eve` / `weekend-roll`（默认值已经照顾到周末）→ `null`
 *     （那种情况下再"建议改成周日"就是废话 —— 框里已经是周日了）；
 *   - 策略是 `same-day` 且这一天是周五/周六 → 给出建议的日期与一句话，
 *     家长端把它渲染成一枚「改成周日」的按钮（点一下 = 采纳）。
 */
export function homeworkDueSuggestion(
  planDate: string,
  policy: HomeworkDuePolicy = HOMEWORK_DUE_POLICY,
  schoolWeekdays: readonly number[] = HOMEWORK_SCHOOL_WEEKDAYS,
): { date: string; text: string } | null {
  if (policy !== 'same-day') return null;
  const weekendDue = defaultHomeworkDueDate(planDate, 'weekend-roll', schoolWeekdays);
  if (weekendDue === planDate) return null;
  const label = homeworkDueDateLabel(weekendDue, planDate);
  const weekday = WEEKDAY_LABELS[weekdayOf(planDate)] ?? '周末';
  return {
    date: weekendDue,
    text: `这一天是${weekday}布置的 —— 作业建议留到 ${label}（整个周末都能做）。`,
  };
}

/**
 * 快捷选项（**按手机点一下就能选**来设计）：
 *  - `当天`（永远有）；
 *  - `默认 …`：只有当默认值与当天不同时才出现（周五/周六 → `默认 周日 10-18`）；
 *  - `明天`：与上面两个都不同时才出现。
 *
 * 去重按 **value**：绝不会出现两个按钮指向同一天（那会让家长以为"点了没反应"）。
 * 任意日期的手动指定由旁边的日期选择器负责 —— 快捷选项只是"不用打开日历"。
 *
 * 注意"当天"的 value 就是 `planDate` 本身（**不是空串**）：
 * 提交的 `dueDate` 永远是具体日期，界面上看到的与存下去的因此永远是同一个值。
 */
export function homeworkDueQuickOptions(
  planDate: string,
  policy: HomeworkDuePolicy = HOMEWORK_DUE_POLICY,
): HomeworkDueQuickOption[] {
  const options: HomeworkDueQuickOption[] = [{ value: planDate, label: '当天' }];
  const seen = new Set<string>([planDate]);

  const fallback = defaultHomeworkDueDate(planDate, policy);
  if (!seen.has(fallback)) {
    seen.add(fallback);
    options.push({ value: fallback, label: `默认 ${homeworkDueDateLabel(fallback, planDate)}` });
  }

  const tomorrow = addDays(planDate, 1);
  if (!seen.has(tomorrow)) {
    seen.add(tomorrow);
    options.push({ value: tomorrow, label: '明天' });
  }

  return options;
}
